// src/App.tsx
import React from "react";
import {AppBar,Toolbar,Typography} from "@mui/material";
import {Container,Box,Button} from "@mui/material";
import {Stack,Paper,TextField} from "@mui/material";

import { Document, Page, pdfjs } from "react-pdf";
import { QRCodeCanvas } from "qrcode.react";

// PDF.js worker
//pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = "lib/pdf.worker.min.mjs";

const QR_SIZE = 100;

interface Position {
  x: number;
  y: number;
}

interface AppState {
  useLoader: boolean;
  pdfFileUrl: string | null;
  numPages: number | null;
  qrValue: string;
  qrVisible: boolean;
  qrPosition: Position;
  isDragging: boolean;
  dragOffset: Position;
}

export class QRTagView extends React.Component<any, AppState> {
  pdfContainerRef: React.RefObject<HTMLDivElement | null>;

  constructor(props: any) {
    super(props);

    const {url,load=false} = props;

    this.state = {
      useLoader: load,
      pdfFileUrl: url,
      numPages: null,
      qrValue: "NTR0000000000",
      qrVisible: false,
      qrPosition: { x: 20, y: 20 },
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
    };

    this.pdfContainerRef = React.createRef();

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }

  // ====== Lifecycle ======
  componentDidUpdate(_: any, prevState: AppState) {
    if (this.state.isDragging && !prevState.isDragging) {
      window.addEventListener("mousemove", this.handleMouseMove);
      window.addEventListener("mouseup", this.handleMouseUp);
    }

    if (!this.state.isDragging && prevState.isDragging) {
      window.removeEventListener("mousemove", this.handleMouseMove);
      window.removeEventListener("mouseup", this.handleMouseUp);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseup", this.handleMouseUp);
  }

  // ====== File upload ======
  handlePdfUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);

    this.setState({
      pdfFileUrl: url,
      numPages: null,
      qrVisible: false,
    });
  };

  // ====== PDF load ======
  onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    this.setState({ numPages, qrVisible: true });
  };

  // ====== Drag Handlers ======
  handleQrMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!this.pdfContainerRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = this.pdfContainerRef.current.getBoundingClientRect();

    const offsetX = event.clientX - (rect.left + this.state.qrPosition.x);
    const offsetY = event.clientY - (rect.top + this.state.qrPosition.y);

    this.setState({
      isDragging: true,
      dragOffset: { x: offsetX, y: offsetY },
    });
  };

  handleMouseMove(event: MouseEvent) {
    if (!this.state.isDragging || !this.pdfContainerRef.current) return;

    const rect = this.pdfContainerRef.current.getBoundingClientRect();
    const { dragOffset } = this.state;

    let newX = event.clientX - rect.left - dragOffset.x;
    let newY = event.clientY - rect.top - dragOffset.y;

    const maxX = rect.width - QR_SIZE;
    const maxY = rect.height - QR_SIZE;

    // clamp
    newX = Math.min(Math.max(newX, 0), maxX);
    newY = Math.min(Math.max(newY, 0), maxY);

    this.setState({ qrPosition: { x: newX, y: newY } });
  }

  handleMouseUp() {
    if (this.state.isDragging) {
      this.setState({ isDragging: false });
    }
  }

  // ====== Confirm Position ======
  handleConfirmPlacement = () => {
    const {qrPosition} = this.state;
    const { x, y } = this.state.qrPosition;
    const rx = Math.round(x);
    const ry = Math.round(y);
    const coord = {x:rx,y:ry};


    // alert(`QR placed at:\nX = ${rx} px\nY = ${ry} px`);
    // console.log("Final QR coordinates:", { x, y });

    const {filename,onSave} = this.props;
    if(typeof onSave === 'function') {
      const value = `${filename} {x:${rx}, y:${ry}}`;
      const file = {};
      onSave(file,coord,value);
    }
  };

  render() {
    const {
      pdfFileUrl,
      numPages,
      qrValue,
      qrVisible,
      qrPosition,
    } = this.state;
    const {load} = this.props;

    return (
      <>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6">
              PDF QR Tag Configuration
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Stack spacing={3}>
            {/* Controls */}
            <Paper sx={{ p: 2 }}>
              <Stack spacing={2}>
                {load? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button variant="contained" component="label">
                    Load PDF
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={this.handlePdfUpload}
                    />
                  </Button>

                  <TextField
                    label="QR Code Text"
                    value={qrValue}
                    onChange={(e) =>
                      this.setState({ qrValue: e.target.value })
                    }
                    size="small"
                    sx={{ width: 300 }}
                  />
                </Stack>
                ):(<Box></Box>)}

                <Stack direction="row" alignItems="center" spacing={4}>
                  <> Drag QR and place as desired... </>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography>QR X: {Math.round(qrPosition.x)}</Typography>
                  <Typography>QR Y: {Math.round(qrPosition.y)}</Typography>

                  <Button
                    variant="outlined"
                    disabled={!pdfFileUrl}
                    onClick={this.handleConfirmPlacement}
                  >
                    Confirm Placement
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* PDF Viewer */}
            <Paper sx={{ p: 2 }}>
              {!pdfFileUrl ? (
                <Typography>Load a PDF to begin.</Typography>
              ) : (
                <Box
                  ref={this.pdfContainerRef}
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    border: "1px solid #ddd",
                  }}
                >
                  <Document
                    file={pdfFileUrl}
                    onLoadSuccess={this.onDocumentLoadSuccess}
                  >
                    <Page pageNumber={1} width={800} />
                  </Document>

                  {qrVisible && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: qrPosition.y,
                        left: qrPosition.x,
                        width: QR_SIZE,
                        height: QR_SIZE,
                        cursor: "grab",
                        userSelect: "none",
                      }}
                      onMouseDown={this.handleQrMouseDown}
                    >
                      <QRCodeCanvas
                        value={qrValue}
                        size={QR_SIZE}
                        includeMargin={false}
                        level="H"
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Stack>
        </Container>
      </>
    );
  }
}

export default QRTagView;
